import { useQuery } from "@apollo/client";
import { Box, Typography } from "@mui/material";
import _ from "lodash";
import { ReactElement, useState } from "react";

import {
  GetOrganizingAnalyticsDashboardDataQuery,
  TrackedJobStatus,
} from "~frontend/__generated__/graphql";
import {
  GET_ORGANIZING_ANALYTICS_DASHBOARD_DATA,
  COMPUTE_ORGANIZING_ANALYTICS_DASHBOARD_DATA,
} from "~frontend/graphql/analyticsDashboardData";
import {
  ANALYTICS_EMBED_FOR_SUBACCOUNT,
  ANALYTICS_EMBED_FOR_TENANT,
} from "~frontend/graphql/analyticsEmbed";
import { Subaccount } from "~frontend/providers/LoggedInUserProvider";
import { useFeatureFlag } from "~frontend/providers/LoggedInUserProvider/context";
import { formatDateTime } from "~frontend/util/dateFormat";

import { AsyncWrapper } from "../AsyncWrapper";
import ErrorCard from "../ErrorCard";
import { FlexBox } from "../FlexBox";
import Loading from "../Loading";
import { AnalyticsDashboardControls } from "./AnalyticsDashboardControls";
import { AnalyticsDataTable } from "./AnalyticsDataTable";
import { AnalyticsEmbed } from "./AnalyticsEmbed";
import { RepliesAndOptOutsChart } from "./RepliesAndOptOutsChart";
import { TopLineStat } from "./TopLineStat";
import { maybeNumberToPercentage } from "./numbers";
import { DEFAULT_TIMEFRAME, Timeframe } from "./timeframes";
import { useAnalyticsDashboardData } from "./useAnalyticsDashboardData";

export function OrganizingAnalytics({
  visible,
  subaccount,
}: {
  visible: boolean;
  subaccount?: Subaccount;
}): ReactElement {
  const analyticsDashboardV2Enabled = useFeatureFlag("analyticsDashboardV2");

  if (!visible) {
    return <></>;
  }

  if (analyticsDashboardV2Enabled) {
    return <OrganizingAnalyticsDashboardV2 subaccount={subaccount} />;
  } else {
    return <OrganizingAnalyticsEmbed subaccount={subaccount} />;
  }
}

function OrganizingAnalyticsDashboardV2({
  subaccount,
}: {
  subaccount?: Subaccount;
}): ReactElement {
  const { timeframe, refresh, loading, data, computeInProgress, errors } =
    useAnalyticsDashboardData<GetOrganizingAnalyticsDashboardDataQuery>({
      dataQuery: GET_ORGANIZING_ANALYTICS_DASHBOARD_DATA,
      dataSelector: data => data.getOrganizingAnalyticsDashboardData,
      computeDataMutation: COMPUTE_ORGANIZING_ANALYTICS_DASHBOARD_DATA,
      subaccount,
    });

  return (
    <>
      <FlexBox justifyContent="space-between">
        {data?.getOrganizingAnalyticsDashboardData ? (
          <Typography variant="body3">
            Last computed at{" "}
            {formatDateTime(
              data.getOrganizingAnalyticsDashboardData.computedAt
            )}
          </Typography>
        ) : (
          <Box sx={{ height: "10px" }} />
        )}
        <AnalyticsDashboardControls
          timeframe={timeframe}
          onSubmit={refresh}
          disabled={loading || computeInProgress}
        />
      </FlexBox>
      <AsyncWrapper data={data} loading={loading} errors={errors}>
        {data => {
          if (
            data.getOrganizingAnalyticsDashboardData?.computeJob?.status ===
            TrackedJobStatus.Failed
          ) {
            return (
              <ErrorCard
                errors={["Compute failed. Please try again."]}
                skipApollo
                onClickReload={() => refresh(timeframe)}
              />
            );
          }

          return (
            <>
              <FlexBox
                flexDirection="column"
                alignItems="flex-start"
                gap="40px"
                sx={{ marginTop: 3 }}
              >
                <FlexBox
                  justifyContent="center"
                  flexWrap="wrap"
                  gap={1}
                  sx={{ width: "100%" }}
                >
                  <TopLineStat
                    label="Texts Sent"
                    value={
                      data.getOrganizingAnalyticsDashboardData?.textsSent?.toLocaleString() ??
                      null
                    }
                  />
                  <TopLineStat
                    label="Reply Rate"
                    value={maybeNumberToPercentage(
                      data.getOrganizingAnalyticsDashboardData?.replyRate,
                      1
                    )}
                  />
                  <TopLineStat
                    label="Opt Out Rate"
                    value={maybeNumberToPercentage(
                      data.getOrganizingAnalyticsDashboardData?.optOutRate,
                      1
                    )}
                  />
                  <TopLineStat
                    label="Conversations"
                    value={
                      data.getOrganizingAnalyticsDashboardData?.conversations?.toLocaleString() ??
                      null
                    }
                  />
                  <TopLineStat
                    label="Active Texters"
                    value={
                      data.getOrganizingAnalyticsDashboardData?.activeTexters?.toLocaleString() ??
                      null
                    }
                  />
                  <TopLineStat
                    label="Tags Applied"
                    value={
                      data.getOrganizingAnalyticsDashboardData?.tagsApplied?.toLocaleString() ??
                      null
                    }
                  />
                </FlexBox>
                <AnalyticsDataTable
                  title="Campaign Organizing Overview - Key Metrics"
                  loading={
                    !data.getOrganizingAnalyticsDashboardData?.campaignOverview
                  }
                  rows={
                    data.getOrganizingAnalyticsDashboardData?.campaignOverview?.map(
                      row => ({ ...row, id: row.campaignId })
                    ) ?? []
                  }
                  columns={[
                    {
                      key: "campaignName",
                      title: "Campaign Name",
                      renderValue: row => row.campaignName,
                      getSortableValue: row => row.campaignName,
                    },
                    {
                      key: "campaignStartDate",
                      title: "Campaign Start Date",
                      renderValue: row => row.campaignStartDate,
                      getSortableValue: row => row.campaignStartDate,
                    },
                    {
                      key: "textsSent",
                      title: "Texts Sent",
                      renderValue: row => row.textsSent.toLocaleString(),
                      getSortableValue: row => row.textsSent,
                    },
                    {
                      key: "activeTexters",
                      title: "Active Texters",
                      renderValue: row => row.activeTexters.toLocaleString(),
                      getSortableValue: row => row.activeTexters,
                    },
                    {
                      key: "conversations",
                      title: "Conversations",
                      renderValue: row => row.conversations.toLocaleString(),
                      getSortableValue: row => row.conversations,
                    },
                    {
                      key: "replyRate",
                      title: "Reply Rate",
                      renderValue: row =>
                        maybeNumberToPercentage(row.replyRate, 2),
                      getSortableValue: row => row.replyRate,
                    },
                    {
                      key: "optOutRate",
                      title: "Opt Out Rate",
                      renderValue: row =>
                        maybeNumberToPercentage(row.optOutRate, 2),
                      getSortableValue: row => row.optOutRate,
                    },
                  ]}
                  downloadableRows={
                    data.getOrganizingAnalyticsDashboardData?.campaignOverview?.map(
                      row => ({
                        campaignId: row.campaignId,
                        campaignName: row.campaignName,
                        campaignStartDate: row.campaignStartDate,
                        textsSent: row.textsSent.toFixed(0),
                        activeTexters: row.textsSent.toFixed(0),
                        conversations: row.conversations.toFixed(0),
                        replyRate: row.replyRate.toFixed(4),
                        optOutRate: row.optOutRate.toFixed(4),
                      })
                    ) ?? []
                  }
                  downloadFileName="campaign_organizing_overview.csv"
                  defaultSortColumnKey="replyRate"
                  defaultSortDirection="desc"
                />
                <AnalyticsDataTable
                  title="Texter Leaderboard"
                  loading={
                    !data.getOrganizingAnalyticsDashboardData?.texterLeaderboard
                  }
                  rows={
                    data.getOrganizingAnalyticsDashboardData?.texterLeaderboard?.map(
                      row => ({ ...row, id: row.userId })
                    ) ?? []
                  }
                  columns={[
                    {
                      key: "fullName",
                      title: "Full Name",
                      renderValue: row => row.fullName,
                      getSortableValue: row => row.fullName,
                    },
                    {
                      key: "subaccountName",
                      title: "Subaccount Name",
                      renderValue: row => row.subaccountName,
                      getSortableValue: row => row.subaccountName,
                    },
                    {
                      key: "joinDate",
                      title: "Joined Date",
                      renderValue: row => row.joinDate,
                      getSortableValue: row => row.joinDate,
                    },
                    {
                      key: "textsSent",
                      title: "Texts Sent",
                      renderValue: row => row.textsSent.toLocaleString(),
                      getSortableValue: row => row.textsSent,
                      getBarchartValue: row => row.textsSent,
                    },
                    {
                      key: "tagsApplied",
                      title: "Tags Applied",
                      renderValue: row => row.tagsApplied.toLocaleString(),
                      getSortableValue: row => row.tagsApplied,
                      getBarchartValue: row => row.tagsApplied,
                    },
                    {
                      key: "conversations",
                      title: "Conversations Handled",
                      renderValue: row => row.conversations.toLocaleString(),
                      getSortableValue: row => row.conversations,
                      getBarchartValue: row => row.conversations,
                    },
                  ]}
                  defaultSortColumnKey="textsSent"
                  defaultSortDirection="desc"
                  downloadableRows={data.getOrganizingAnalyticsDashboardData?.texterLeaderboard?.map(
                    row => ({
                      userId: row.userId,
                      fullName: row.fullName,
                      subaccountName: row.subaccountName,
                      joinDate: row.joinDate,
                      textsSent: row.textsSent.toFixed(0),
                      tagsApplied: row.tagsApplied.toFixed(0),
                      conversations: row.conversations.toFixed(0),
                    })
                  )}
                  downloadFileName="texterLeaderboard.csv"
                />
                <AnalyticsDataTable
                  title="Top Tags"
                  loading={!data.getOrganizingAnalyticsDashboardData?.topTags}
                  rows={
                    data.getOrganizingAnalyticsDashboardData?.topTags?.map(
                      row => ({ ...row, id: row.tagId })
                    ) ?? []
                  }
                  columns={[
                    {
                      key: "rank",
                      title: "Rank",
                      renderValue: row => row.rank.toLocaleString(),
                      getSortableValue: row => row.rank,
                      renderFooter: () => "Total",
                    },
                    {
                      key: "name",
                      title: "Name",
                      renderValue: row => row.tagName,
                      getSortableValue: row => row.tagName,
                    },
                    {
                      key: "campaignContactTags",
                      title: "Campaign Contact Tags",
                      renderValue: row =>
                        row.taggedCampaignContacts.toLocaleString(),
                      getSortableValue: row => row.taggedCampaignContacts,
                      getBarchartValue: row => row.taggedCampaignContacts,
                      renderFooter: () =>
                        _.sumBy(
                          data.getOrganizingAnalyticsDashboardData?.topTags,
                          "taggedCampaignContacts"
                        ).toLocaleString(),
                    },
                    {
                      key: "percentage",
                      title: "Percent of Tags",
                      renderValue: row =>
                        maybeNumberToPercentage(
                          row.taggedCampaignContacts /
                            _.sumBy(
                              data.getOrganizingAnalyticsDashboardData!
                                .topTags!,
                              "taggedCampaignContacts"
                            )
                        ),
                      getSortableValue: row => row.taggedCampaignContacts,
                      renderFooter: () => "100%",
                    },
                  ]}
                  defaultSortColumnKey="rank"
                  defaultSortDirection="asc"
                  downloadableRows={data.getOrganizingAnalyticsDashboardData?.topTags?.map(
                    row => ({
                      tagId: row.tagId,
                      tagName: row.tagName,
                      taggedCampaignContacts:
                        row.taggedCampaignContacts.toFixed(0),
                    })
                  )}
                  downloadFileName="top_tags.csv"
                />
                <RepliesAndOptOutsChart
                  data={
                    data.getOrganizingAnalyticsDashboardData
                      ?.repliesAndOptOuts ?? null
                  }
                />
              </FlexBox>
            </>
          );
        }}
      </AsyncWrapper>
    </>
  );
}

function OrganizingAnalyticsEmbed({
  subaccount,
}: {
  subaccount?: Subaccount;
}): ReactElement {
  const [timeframe, setTimeframe] = useState<Timeframe>(DEFAULT_TIMEFRAME);

  const query = subaccount
    ? ANALYTICS_EMBED_FOR_SUBACCOUNT
    : ANALYTICS_EMBED_FOR_TENANT;

  const subaccountId = subaccount?.id;

  const { loading, error, data, refetch } = useQuery(query, {
    variables: {
      subaccountId: subaccountId,
      timeframe,
      dashboardPath: "/embed/dashboards/8",
    },
    fetchPolicy: "network-only",
  });

  if (loading) {
    return <Loading />;
  }
  if (error) {
    return <ErrorCard errors={[error]} />;
  }

  const iframeUrl = subaccount
    ? data.analyticsEmbedForSubaccount.signedEmbedUrl
    : data.analyticsEmbedForTenant.signedEmbedUrl;

  const lookerSessionExpiresAt = subaccount
    ? data.analyticsEmbedForSubaccount.expiresAt
    : data.analyticsEmbedForTenant.expiresAt;

  return (
    <AnalyticsEmbed
      id="organizing-embed"
      timeframe={timeframe}
      onChangeTimeframe={setTimeframe}
      iframeUrl={iframeUrl}
      expiresAt={lookerSessionExpiresAt}
      refresh={refetch}
    />
  );
}
